import {isNativeCapacitorPlatform} from '../platform';
import {PUBLISHED_GUIDE_SLUGS} from '../../guides/published-guide-slugs';

export type AppLanguageCode = 'en' | 'de' | 'uk' | 'ru';

export type AppLanguageMenuCode = 'EN' | 'DE' | 'UK' | 'RU';

export const DEFAULT_APP_LANGUAGE_CODE: AppLanguageCode = 'en';

export const SUPPORTED_APP_LANGUAGE_CODES: readonly AppLanguageCode[] = ['en', 'de', 'uk', 'ru'];

const LOCALIZED_ROUTE_LANGUAGE_CODES: readonly AppLanguageCode[] = ['en', 'de', 'uk', 'ru'];

const SERVER_RENDERED_GUIDE_PATHNAMES = new Set<string>();
for (const language of SUPPORTED_APP_LANGUAGE_CODES) {
  SERVER_RENDERED_GUIDE_PATHNAMES.add(`/${language}/guides`);
  for (const slug of PUBLISHED_GUIDE_SLUGS) {
    SERVER_RENDERED_GUIDE_PATHNAMES.add(`/${language}/guides/${slug}`);
  }
}

export function normalizeAppLanguageCode(value: string | null | undefined): AppLanguageCode | null {
  const normalized = value?.trim().toLowerCase();
  const normalizedLanguage = normalized?.split(/[\-_]/, 1)[0];
  if (normalizedLanguage === 'en' || normalizedLanguage === 'de' || normalizedLanguage === 'uk' || normalizedLanguage === 'ru') {
    return normalizedLanguage;
  }

  if (normalized === 'ua') {
    return 'uk';
  }

  return null;
}

export function extractLocalizedRouteLanguageFromPathname(pathname: string): AppLanguageCode | null {
  const firstSegment = pathname
    .trim()
    .split('/')
    .filter(Boolean)[0]
    ?.toLowerCase();

  if (firstSegment && LOCALIZED_ROUTE_LANGUAGE_CODES.includes(firstSegment as AppLanguageCode)) {
    return firstSegment as AppLanguageCode;
  }

  return null;
}

export function extractLocalizedRouteLanguageFromUrl(url: string): AppLanguageCode | null {
  try {
    return extractLocalizedRouteLanguageFromPathname(new URL(url, 'http://localhost').pathname);
  } catch {
    return extractLocalizedRouteLanguageFromPathname(url);
  }
}

export function stripLocalizedRouteLanguageFromPathname(pathname: string): string {
  const segments = pathname.trim().split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/';
  }

  const language = extractLocalizedRouteLanguageFromPathname(pathname);
  if (!language) {
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }

  const remainingSegments = segments.slice(1);
  return remainingSegments.length > 0 ? `/${remainingSegments.join('/')}` : '/';
}

export function buildLocalizedRoutePathname(pathname: string, language: AppLanguageCode): string {
  const resolvedLanguage = normalizeAppLanguageCode(language) ?? DEFAULT_APP_LANGUAGE_CODE;
  const segments = pathname.trim().split('/').filter(Boolean);
  const currentLanguage = extractLocalizedRouteLanguageFromPathname(pathname);
  const pathSegments = currentLanguage ? segments.slice(1) : segments;

  return pathSegments.length > 0 ? `/${resolvedLanguage}/${pathSegments.join('/')}` : `/${resolvedLanguage}`;
}

export function buildPlatformAwareRoutePathname(pathname: string, language: AppLanguageCode): string {
  if (typeof window === 'undefined') {
    return pathname;
  }

  return pathname.startsWith('/') && !isNativeCapacitorPlatform()
    ? buildLocalizedRoutePathname(pathname, language)
    : pathname;
}

export function isServerRenderedRoutePathname(pathname: string): boolean {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const routePathname = stripLocalizedRouteLanguageFromPathname(normalizedPathname);

  if (SERVER_RENDERED_GUIDE_PATHNAMES.has(normalizedPathname)) {
    return true;
  }

  return routePathname === '/'
    || routePathname === '/about'
    || routePathname === '/whats-new'
    || routePathname === '/terms'
    || routePathname === '/privacy'
    || routePathname === '/account-deletion'
    || routePathname === '/ssr-demo'
    || routePathname === '/items'
    || /^\/items\/[^/]+\/[^/]+$/.test(routePathname);
}

export function isLocalizedPublicRouteUrl(url: string): boolean {
  const pathname = new URL(url, 'http://localhost').pathname;
  return extractLocalizedRouteLanguageFromPathname(pathname) !== null
    && (isServerRenderedRoutePathname(pathname)
      || stripLocalizedRouteLanguageFromPathname(pathname).replace(/\/+$/, '') === '/checkout');
}

export function toAppLanguageMenuCode(languageCode: AppLanguageCode): AppLanguageMenuCode {
  switch (languageCode) {
    case 'en':
      return 'EN';
    case 'de':
      return 'DE';
    case 'uk':
      return 'UK';
    case 'ru':
      return 'RU';
  }
}

export function fromAppLanguageMenuCode(languageCode: AppLanguageMenuCode): AppLanguageCode {
  switch (languageCode) {
    case 'EN':
      return 'en';
    case 'DE':
      return 'de';
    case 'UK':
      return 'uk';
    case 'RU':
      return 'ru';
  }
}
